import React, { useState } from 'react';
import { PageHeader } from '../../components/layout/PageHeader';
import { Card } from '../../components/ui/Card';
import { Button } from '../../components/ui/Button';
import { Tabs } from '../../components/ui/Tabs';
import { StatusPill } from '../../components/ui/StatusPill';
import { Badge } from '../../components/ui/Badge';
import { suppliers } from '../../data/suppliers';
import { BuildingIcon, MapPinIcon, GlobeIcon, PhoneIcon, MailIcon, EditIcon, ShieldCheckIcon, CreditCardIcon, UsersIcon, AwardIcon } from 'lucide-react';
export const CompanyProfile: React.FC = () => {
  const [activeTab, setActiveTab] = useState('legal');
  // Demo supplier
  const supplier = suppliers.find(s => s.id === 'SUP-001');
  if (!supplier) return null;
  return <div className="max-w-7xl mx-auto space-y-6 pb-12" data-id="element-801">
      <PageHeader title="Company Profile" description="Manage your legal entity data, addresses, and contacts." breadcrumbs={[{
      label: 'Home',
      href: '/supplier/dashboard'
    }, {
      label: 'Company Profile'
    }]} primaryAction={<Button variant="secondary" leftIcon={<EditIcon className="w-4 h-4" data-id="element-804" />} data-id="element-803">
            Edit Profile
          </Button>} data-id="element-802" />

      {/* Profile Header Card */}
      <Card className="flex flex-col md:flex-row items-start md:items-center gap-6" data-id="element-805">
        <div className="w-20 h-20 bg-fpg-navy-light/10 text-fpg-navy rounded-fluent-xl flex items-center justify-center flex-shrink-0 border border-fpg-navy/20" data-id="element-806">
          <BuildingIcon className="w-10 h-10" data-id="element-807" />
        </div>
        <div className="flex-1" data-id="element-808">
          <div className="flex flex-col md:flex-row md:items-center gap-3 mb-2" data-id="element-809">
            <h2 className="text-2xl font-bold text-text-primary" data-id="element-810">
              {supplier.name}
            </h2>
            <StatusPill status={supplier.status} data-id="element-811" />
            <Badge variant="info" data-id="element-812">{supplier.supplierType} Supplier</Badge>
          </div>
          <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm text-text-secondary" data-id="element-813">
            <span className="flex items-center gap-1.5" data-id="element-814">
              <MapPinIcon className="w-4 h-4" data-id="element-815" /> {supplier.city},{' '}
              {supplier.country}
            </span>
            <span className="flex items-center gap-1.5" data-id="element-816">
              <GlobeIcon className="w-4 h-4" data-id="element-817" /> {supplier.website}
            </span>
            <span className="flex items-center gap-1.5" data-id="element-818">
              <MailIcon className="w-4 h-4" data-id="element-819" /> {supplier.primaryContactEmail}
            </span>
            <span className="flex items-center gap-1.5" data-id="element-820">
              <PhoneIcon className="w-4 h-4" data-id="element-821" /> {supplier.phone}
            </span>
          </div>
        </div>
      </Card>

      <Card noPadding className="flex flex-col min-h-[500px]" data-id="element-822">
        <div className="px-6 pt-4 border-b border-border-light bg-surface-secondary/50" data-id="element-823">
          <Tabs tabs={[{
          id: 'legal',
          label: 'Legal Entity & Details'
        }, {
          id: 'tax-bank',
          label: 'Tax & Banking'
        }, {
          id: 'contacts',
          label: 'Contacts'
        }, {
          id: 'certifications',
          label: 'Certifications & ESG'
        }]} activeTab={activeTab} onChange={setActiveTab} data-id="element-824" />
        </div>

        <div className="p-6 flex-1" data-id="element-825">
          {activeTab === 'legal' && <div className="space-y-8 max-w-4xl" data-id="element-826">
              <section data-id="element-827">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2" data-id="element-828">
                  <BuildingIcon className="w-5 h-5 text-text-tertiary" data-id="element-829" />
                  Company Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-830">
                  <div data-id="element-831">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-832">
                      Legal Company Name
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-833">
                      {supplier.legalName}
                    </div>
                  </div>
                  <div data-id="element-834">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-835">
                      Trading Name
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-836">
                      {supplier.name}
                    </div>
                  </div>
                  <div data-id="element-837">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-838">
                      Primary Category
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-839">
                      {supplier.category}
                    </div>
                  </div>
                  <div data-id="element-840">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-841">
                      Subcategory
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-842">
                      {supplier.subcategory}
                    </div>
                  </div>
                  <div data-id="element-843">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-844">
                      Registration Date
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-845">
                      {supplier.registrationDate}
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-border-light" data-id="element-846" />

              <section data-id="element-847">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2" data-id="element-848">
                  <MapPinIcon className="w-5 h-5 text-text-tertiary" data-id="element-849" />
                  Headquarters Address
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-850">
                  <div className="md:col-span-2" data-id="element-851">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-852">
                      Street Address
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-853">
                      123 Fishery Port Road, #02-15
                    </div>
                  </div>
                  <div data-id="element-854">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-855">
                      City
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-856">
                      {supplier.city}
                    </div>
                  </div>
                  <div data-id="element-857">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-858">
                      Postal Code
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-859">
                      619746
                    </div>
                  </div>
                  <div data-id="element-860">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-861">
                      Country
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-862">
                      {supplier.country}
                    </div>
                  </div>
                </div>
              </section>
            </div>}

          {activeTab === 'tax-bank' && <div className="space-y-8 max-w-4xl" data-id="element-863">
              <section data-id="element-864">
                <div className="flex justify-between items-center mb-4" data-id="element-865">
                  <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2" data-id="element-866">
                    <ShieldCheckIcon className="w-5 h-5 text-text-tertiary" data-id="element-867" />
                    Tax Information
                  </h3>
                  <Badge variant="success" showDot data-id="element-868">
                    Verified
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-869">
                  <div data-id="element-870">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-871">
                      Tax Registration Number (UEN)
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-872">
                      {supplier.taxId}
                    </div>
                  </div>
                  <div data-id="element-873">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-874">
                      Tax Country
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-875">
                      {supplier.country}
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-border-light" data-id="element-876" />

              <section data-id="element-877">
                <div className="flex justify-between items-center mb-4" data-id="element-878">
                  <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2" data-id="element-879">
                    <CreditCardIcon className="w-5 h-5 text-text-tertiary" data-id="element-880" />
                    Primary Bank Account
                  </h3>
                  <Badge variant="success" showDot data-id="element-881">
                    Active
                  </Badge>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-id="element-882">
                  <div data-id="element-883">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-884">
                      Bank Name
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-885">
                      DBS Bank Ltd
                    </div>
                  </div>
                  <div data-id="element-886">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-887">
                      Account Currency
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light" data-id="element-888">
                      SGD
                    </div>
                  </div>
                  <div data-id="element-889">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-890">
                      Account Number
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light font-mono" data-id="element-891">
                      {supplier.bankAccountMasked}
                    </div>
                  </div>
                  <div data-id="element-892">
                    <label className="block text-xs font-semibold text-text-secondary uppercase tracking-wider mb-1" data-id="element-893">
                      SWIFT / BIC
                    </label>
                    <div className="text-sm text-text-primary p-2.5 bg-surface-secondary rounded border border-border-light font-mono" data-id="element-894">
                      DBSSSGSG
                    </div>
                  </div>
                </div>
                <div className="mt-4" data-id="element-895">
                  <Button variant="secondary" size="sm" data-id="element-896">
                    Request Bank Change
                  </Button>
                </div>
              </section>
            </div>}

          {activeTab === 'contacts' && <div className="space-y-6" data-id="element-897">
              <div className="flex justify-between items-center" data-id="element-898">
                <h3 className="text-lg font-semibold text-text-primary flex items-center gap-2" data-id="element-899">
                  <UsersIcon className="w-5 h-5 text-text-tertiary" data-id="element-900" />
                  Key Contacts
                </h3>
                <Button variant="secondary" size="sm" data-id="element-901">
                  Add Contact
                </Button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-id="element-902">
                {/* Primary Contact */}
                <div className="border border-border-light rounded-fluent-lg p-4 bg-surface-primary shadow-elevation-1" data-id="element-903">
                  <div className="flex justify-between items-start mb-3" data-id="element-904">
                    <div data-id="element-905">
                      <h4 className="font-semibold text-text-primary" data-id="element-906">
                        {supplier.primaryContact}
                      </h4>
                      <p className="text-sm text-text-secondary" data-id="element-907">
                        Primary Account Manager
                      </p>
                    </div>
                    <Badge variant="info" data-id="element-908">Primary</Badge>
                  </div>
                  <div className="space-y-2 text-sm text-text-secondary" data-id="element-909">
                    <p className="flex items-center gap-2" data-id="element-910">
                      <MailIcon className="w-4 h-4" data-id="element-911" />{' '}
                      {supplier.primaryContactEmail}
                    </p>
                    <p className="flex items-center gap-2" data-id="element-912">
                      <PhoneIcon className="w-4 h-4" data-id="element-913" /> {supplier.phone}
                    </p>
                  </div>
                </div>

                {/* Finance Contact */}
                <div className="border border-border-light rounded-fluent-lg p-4 bg-surface-primary" data-id="element-914">
                  <div className="flex justify-between items-start mb-3" data-id="element-915">
                    <div data-id="element-916">
                      <h4 className="font-semibold text-text-primary" data-id="element-917">
                        Sarah Lee
                      </h4>
                      <p className="text-sm text-text-secondary" data-id="element-918">
                        Finance & AR Manager
                      </p>
                    </div>
                    <Badge variant="neutral" data-id="element-919">Finance</Badge>
                  </div>
                  <div className="space-y-2 text-sm text-text-secondary" data-id="element-920">
                    <p className="flex items-center gap-2" data-id="element-921">
                      <MailIcon className="w-4 h-4" data-id="element-922" /> finance@oceanicharvest.sg
                    </p>
                    <p className="flex items-center gap-2" data-id="element-923">
                      <PhoneIcon className="w-4 h-4" data-id="element-924" /> +65 6234 5679
                    </p>
                  </div>
                </div>
              </div>
            </div>}

          {activeTab === 'certifications' && <div className="space-y-8" data-id="element-925">
              <section data-id="element-926">
                <h3 className="text-lg font-semibold text-text-primary mb-4 flex items-center gap-2" data-id="element-927">
                  <AwardIcon className="w-5 h-5 text-text-tertiary" data-id="element-928" />
                  ESG & Diversity
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-id="element-929">
                  <div className="p-4 bg-surface-secondary rounded-fluent border border-border-light" data-id="element-930">
                    <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-1" data-id="element-931">
                      ESG Rating
                    </p>
                    <p className="text-2xl font-bold text-fpg-green" data-id="element-932">
                      {supplier.esgRating}
                    </p>
                    <p className="text-xs text-text-secondary mt-1" data-id="element-933">
                      Assessed 2025
                    </p>
                  </div>
                  <div className="p-4 bg-surface-secondary rounded-fluent border border-border-light md:col-span-2" data-id="element-934">
                    <p className="text-xs text-text-secondary uppercase tracking-wider font-semibold mb-2" data-id="element-935">
                      Diversity Attributes
                    </p>
                    <div className="flex flex-wrap gap-2" data-id="element-936">
                      {supplier.diversityAttributes.map(attr => <Badge key={attr} variant="info" data-id="element-937">
                          {attr}
                        </Badge>)}
                      {supplier.diversityAttributes.length === 0 && <span className="text-sm text-text-secondary" data-id="element-938">
                          None specified
                        </span>}
                    </div>
                  </div>
                </div>
              </section>

              <hr className="border-border-light" data-id="element-939" />

              <section data-id="element-940">
                <div className="flex justify-between items-center mb-4" data-id="element-941">
                  <h3 className="text-lg font-semibold text-text-primary" data-id="element-942">
                    Compliance Documents
                  </h3>
                  <Button variant="secondary" size="sm" data-id="element-943">
                    Upload Document
                  </Button>
                </div>

                <div className="border border-border-light rounded-fluent-lg overflow-hidden" data-id="element-944">
                  <table className="w-full text-left text-sm" data-id="element-945">
                    <thead className="bg-surface-secondary border-b border-border-light" data-id="element-946">
                      <tr data-id="element-947">
                        <th className="px-4 py-3 font-semibold text-text-secondary" data-id="element-948">
                          Document Type
                        </th>
                        <th className="px-4 py-3 font-semibold text-text-secondary" data-id="element-949">
                          Status
                        </th>
                        <th className="px-4 py-3 font-semibold text-text-secondary" data-id="element-950">
                          Expiry Date
                        </th>
                        <th className="px-4 py-3 font-semibold text-text-secondary text-right" data-id="element-951">
                          Action
                        </th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-border-light" data-id="element-952">
                      <tr data-id="element-953">
                        <td className="px-4 py-3 font-medium" data-id="element-954">
                          Business Registration (ACRA)
                        </td>
                        <td className="px-4 py-3" data-id="element-955">
                          <Badge variant="success" data-id="element-956">Valid</Badge>
                        </td>
                        <td className="px-4 py-3" data-id="element-957">
                          {supplier.complianceExpiry}
                        </td>
                        <td className="px-4 py-3 text-right" data-id="element-958">
                          <Button variant="ghost" size="sm" data-id="element-959">
                            View
                          </Button>
                        </td>
                      </tr>
                      <tr data-id="element-960">
                        <td className="px-4 py-3 font-medium" data-id="element-961">
                          Halal Certification
                        </td>
                        <td className="px-4 py-3" data-id="element-962">
                          <Badge variant="warning" data-id="element-963">Expiring Soon</Badge>
                        </td>
                        <td className="px-4 py-3 text-status-warning font-medium" data-id="element-964">
                          2026-04-15
                        </td>
                        <td className="px-4 py-3 text-right" data-id="element-965">
                          <Button variant="secondary" size="sm" data-id="element-966">
                            Update
                          </Button>
                        </td>
                      </tr>
                      <tr data-id="element-967">
                        <td className="px-4 py-3 font-medium" data-id="element-968">
                          Supplier Code of Conduct
                        </td>
                        <td className="px-4 py-3" data-id="element-969">
                          <Badge variant="success" data-id="element-970">Signed</Badge>
                        </td>
                        <td className="px-4 py-3 text-text-tertiary" data-id="element-971">N/A</td>
                        <td className="px-4 py-3 text-right" data-id="element-972">
                          <Button variant="ghost" size="sm" data-id="element-973">
                            View
                          </Button>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              </section>
            </div>}
        </div>
      </Card>
    </div>;
};